import { Box, Button, Checkbox, Flex, Input, Progress, Text } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormSuccess } from '../../../components/FormSuccess'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import VideoFileInput from '../../../components/VideoFileInput/VideoFileInput'
import { capitalizeName, EMAIL_REGEX, hasFullName, useAppToast } from '../../../helpers'
import { TestimonialVideoService } from '../../../services/TestimonialVideo/TestimonialVideoService'
import {
  ITestimonialVideoAddress,
  ITestimonialVideoCourseOption,
  TestimonialVideoModality
} from '../../../services/TestimonialVideo/types'
import AddressFields from './AddressFields'
import {
  EMPTY_ADDRESS,
  MAX_VIDEO_SIZE_MB,
  MODALITY_OPTIONS,
  trimAddress,
  validateAddress,
  validationErrorsOf
} from './consts'
import CoursesLoadError from './CoursesLoadError'

const TITLE = 'Envio de Vídeo de Depoimento'

const emptyForm = {
  name: '',
  email: '',
  emailConfirmation: '',
  modality: '' as '' | TestimonialVideoModality,
  courseId: ''
}

export default function TestimonialVideoSubmitPage() {
  const service = useMemo(() => new TestimonialVideoService(), [])
  const toast = useAppToast()

  const [form, setForm] = useState({ ...emptyForm })
  const [address, setAddress] = useState<ITestimonialVideoAddress>({ ...EMPTY_ADDRESS })
  const [video, setVideo] = useState<File | null>(null)
  const [consent, setConsent] = useState(false)
  const [consentText, setConsentText] = useState<string | null>(null)
  const [consentLoadFailed, setConsentLoadFailed] = useState(false)
  const [coursesByModality, setCoursesByModality] = useState<Record<string, ITestimonialVideoCourseOption[]>>({})
  // A failed load is not cached: picking the modality again retries it.
  const [failedModalities, setFailedModalities] = useState<string[]>([])
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [uploadPercent, setUploadPercent] = useState(0)
  const [submitted, setSubmitted] = useState(false)

  const error = (key: string) => formErrors[key]?.[0]

  // The text on screen is the one the API saves with the submission.
  const loadConsentTerm = useCallback(() => {
    setConsentLoadFailed(false)
    service
      .getConsentTerm()
      .then(setConsentText)
      .catch(() => setConsentLoadFailed(true))
  }, [service])

  useEffect(() => {
    loadConsentTerm()
  }, [loadConsentTerm])

  // Avoids two requests for the same modality when the student switches quickly.
  const loadingModalitiesRef = useRef(new Set<string>())

  const loadCourses = (modality: TestimonialVideoModality) => {
    if (coursesByModality[modality] || loadingModalitiesRef.current.has(modality)) return
    loadingModalitiesRef.current.add(modality)
    setFailedModalities((current) => current.filter((item) => item !== modality))
    service
      .getFormCourses(modality)
      .then((courses) => setCoursesByModality((current) => ({ ...current, [modality]: courses })))
      .catch((err) => {
        setFailedModalities((current) => [...current, modality])
        toast.error('Erro ao carregar os cursos', err)
      })
      .finally(() => loadingModalitiesRef.current.delete(modality))
  }

  const validate = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {}

    if (!form.name.trim()) errors.name = ['O nome é obrigatório.']
    else if (!hasFullName(form.name)) errors.name = ['Informe o nome e sobrenome.']
    if (!form.email.trim()) errors.email = ['O e-mail é obrigatório.']
    else if (!EMAIL_REGEX.test(form.email.trim())) errors.email = ['Informe um e-mail válido.']
    else if (form.email.trim() !== form.emailConfirmation.trim()) {
      errors.email_confirmation = ['A confirmação do e-mail não confere.']
    }
    Object.assign(errors, validateAddress(address))
    if (!form.modality) errors.modality = ['A modalidade é obrigatória.']
    if (!form.courseId) errors.course_id = ['O curso é obrigatório.']
    if (!video) errors.video = ['O vídeo do depoimento é obrigatório.']
    if (!consent) errors.consent = ['É necessário aceitar o termo para enviar o vídeo.']

    return errors
  }

  const buildFormData = (): FormData => {
    const data = new FormData()
    const trimmedAddress = trimAddress(address)

    data.append('name', form.name.trim())
    data.append('email', form.email.trim())
    data.append('email_confirmation', form.emailConfirmation.trim())
    Object.entries(trimmedAddress).forEach(([key, value]) => {
      if (value) data.append(key, value)
    })
    data.append('modality', form.modality)
    data.append('course_id', form.courseId)
    data.append('consent', '1')
    data.append('video', video as File)

    return data
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormErrors({})

    const clientErrors = validate()
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors)
      toast.invalidForm(Object.values(clientErrors)[0][0])
      return
    }

    setSubmitting(true)
    setUploadPercent(0)
    try {
      await service.submit(buildFormData(), setUploadPercent)
      setSubmitted(true)
    } catch (err) {
      const fieldErrors = validationErrorsOf(err)
      if (fieldErrors) {
        setFormErrors(fieldErrors)
        toast.invalidForm(Object.values(fieldErrors)[0]?.[0])
      } else {
        toast.error('Erro ao enviar o vídeo', err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const resetForm = () => {
    setForm({ ...emptyForm })
    setAddress({ ...EMPTY_ADDRESS })
    setVideo(null)
    setConsent(false)
    setFormErrors({})
    setUploadPercent(0)
    // Lists that loaded stay cached; a modality that failed gets a fresh try.
    setFailedModalities([])
    setSubmitted(false)
  }

  if (submitted) {
    return (
      <PublicFormLayout title={TITLE}>
        <FormSuccess
          title="Vídeo enviado!"
          description="Recebemos o seu depoimento. Obrigado por compartilhar a sua experiência com a ABRA!"
          actions={[{ label: 'Enviar outro vídeo', onClick: resetForm, variant: 'outline' }]}
        />
      </PublicFormLayout>
    )
  }

  return (
    <PublicFormLayout title={TITLE}>
      <form onSubmit={handleSubmit}>
        <FormField label="Nome completo" error={error('name')} isRequired>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onBlur={(e) => setForm((prev) => ({ ...prev, name: capitalizeName(e.target.value) }))}
            isDisabled={submitting}
            autoComplete="name"
          />
        </FormField>

        <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
          <FormField label="E-mail" error={error('email')} isRequired flex={1}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              isDisabled={submitting}
              autoComplete="email"
            />
          </FormField>
          <FormField label="Confirme seu e-mail" error={error('email_confirmation')} isRequired flex={1}>
            <Input
              type="email"
              value={form.emailConfirmation}
              onChange={(e) => setForm({ ...form, emailConfirmation: e.target.value })}
              isDisabled={submitting}
              autoComplete="email"
            />
          </FormField>
        </Flex>

        <AddressFields
          value={address}
          onChange={(patch) => setAddress((prev) => ({ ...prev, ...patch }))}
          error={error}
          isDisabled={submitting}
        />

        <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
          <FormField label="Modalidade" error={error('modality')} isRequired flex={1}>
            <FormSelect
              value={form.modality}
              onChange={(value) => {
                const modality = value as typeof form.modality
                setForm({ ...form, modality, courseId: '' })
                if (modality) loadCourses(modality)
              }}
              options={MODALITY_OPTIONS}
              placeholder="Selecione a modalidade"
              isDisabled={submitting}
            />
          </FormField>
          <FormField label="Curso" error={error('course_id')} isRequired flex={2}>
            <SearchableSelect
              placeholder={form.modality ? 'Selecione o curso...' : 'Selecione a modalidade primeiro'}
              value={form.courseId}
              onChange={(value) => setForm({ ...form, courseId: value })}
              options={(form.modality ? (coursesByModality[form.modality] ?? []) : []).map((course) => ({
                value: String(course.id),
                label: course.name
              }))}
              isLoading={
                Boolean(form.modality) && !coursesByModality[form.modality] && !failedModalities.includes(form.modality)
              }
              isDisabled={submitting || !form.modality}
              entity="curso"
            />
            {form.modality && failedModalities.includes(form.modality) && (
              <CoursesLoadError
                onRetry={() => loadCourses(form.modality as TestimonialVideoModality)}
                isDisabled={submitting}
              />
            )}
          </FormField>
        </Flex>

        <Box mt={4}>
          <VideoFileInput
            label="Vídeo do depoimento"
            file={video}
            onChange={setVideo}
            maxSizeMB={MAX_VIDEO_SIZE_MB}
            helperText={`Arquivo MP4 de até ${MAX_VIDEO_SIZE_MB} MB. Dica: grave em 1080p (Full HD) para o arquivo não ficar grande demais.`}
            error={formErrors.video}
            isDisabled={submitting}
            isRequired
          />
        </Box>

        <FormField mt={6} error={error('consent')}>
          {consentText ? (
            <Checkbox
              isChecked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              isDisabled={submitting}
              alignItems="flex-start"
            >
              <Text fontSize="sm">{consentText}</Text>
            </Checkbox>
          ) : consentLoadFailed ? (
            <Flex align="center" gap={3} wrap="wrap">
              <Text fontSize="sm" color="red.500">
                Não foi possível carregar o termo de autorização.
              </Text>
              <Button size="sm" variant="outline" onClick={loadConsentTerm}>
                Tentar novamente
              </Button>
            </Flex>
          ) : (
            <Text variant="caption">Carregando o termo de autorização...</Text>
          )}
        </FormField>

        {submitting && (
          <Box mt={6}>
            <Progress value={uploadPercent} colorScheme="magenta" borderRadius="md" hasStripe isAnimated />
            <Text variant="caption" mt={2} textAlign="center">
              {uploadPercent < 100
                ? `Enviando vídeo... ${uploadPercent}%`
                : 'Finalizando o envio, aguarde alguns instantes...'}
            </Text>
          </Box>
        )}

        <Button
          colorScheme="magenta"
          type="submit"
          w="100%"
          size="lg"
          mt={6}
          isLoading={submitting}
          isDisabled={!consentText}
        >
          Enviar vídeo
        </Button>
      </form>
    </PublicFormLayout>
  )
}
